import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { UserService } from './service/user.service';
import { environment } from '../environments/environment';
import { HttpErrorInterceptor } from 'src/app/service/http-error.interceptor';
import { WholesaleAPIAuthService } from './service/wholesale-commitment-api-auth.service';
import { LoginService } from './service/login.service';
import { WholesaleConstants } from './constants/wholesale.constants';
import { GetAllocationMarketDataService } from './service/get-allocation-market-data.service';
import { MsalService, MsalBroadcastService } from '@azure/msal-angular';
import { AuthenticationResult, EventMessage, EventType, InteractionStatus } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';

/*
This component handles the OAuth callback from Entra ID (Azure AD) using MSAL.
It retrieves tokens from MSAL and stores session variables for the application.
 */
@Component({
	standalone: false,
	template: `
		<h3>Redirecting to requested URL</h3>
	`,
	styles: ['h3 { text-align: center; display: grid; place-items: center; height: 100%;}']
})
export class OAuthCallbackComponent implements OnInit, OnDestroy {
	private userService: UserService;
	private switzerlandSelectLanguage: any[];
	private marketLanguageMapping: any[];
	private marketInfo: MarketInfoModel;
	public isServerError: boolean = false;
	public errorMsg: string;
	public authToken: string;
	private readonly _destroying$ = new Subject<void>();

	constructor(
		private _router: Router,
		private httpInterceptorService: HttpErrorInterceptor,
		private wholesaleAPIAuthService: WholesaleAPIAuthService,
		private loginService: LoginService,
		private getAllocationMarketDataService: GetAllocationMarketDataService,
		private msalService: MsalService,
		private msalBroadcastService: MsalBroadcastService
	) {
	}

	ngOnInit() {
		// Handle MSAL redirect callback
		this.msalService.handleRedirectObservable().subscribe({
			next: (result: AuthenticationResult) => {
				if (result) {
					// Successfully received tokens from redirect
					this.processAuthenticationResult(result);
				} else {
					// No result from redirect, check if user is already logged in
					this.checkAndProcessExistingSession();
				}
			},
			error: (error) => {
				console.error('MSAL redirect error:', error);
				this._router.navigate(['/login']);
			}
		});

		// Listen for login success events
		this.msalBroadcastService.msalSubject$
			.pipe(
				filter((msg: EventMessage) => msg.eventType === EventType.LOGIN_SUCCESS),
				takeUntil(this._destroying$)
			)
			.subscribe((result: EventMessage) => {
				const payload = result.payload as AuthenticationResult;
				this.processAuthenticationResult(payload);
			});

		// Wait for interaction to complete
		this.msalBroadcastService.inProgress$
			.pipe(
				filter((status: InteractionStatus) => status === InteractionStatus.None),
				takeUntil(this._destroying$)
			)
			.subscribe(() => {
				this.checkAndProcessExistingSession();
			});
	}

	ngOnDestroy(): void {
		this._destroying$.next(undefined);
		this._destroying$.complete();
	}

	private checkAndProcessExistingSession(): void {
		const accounts = this.msalService.instance.getAllAccounts();
		if (accounts.length > 0) {
			// User is already logged in, acquire token silently
			this.acquireTokenSilent();
		}
	}

	private async acquireTokenSilent(): Promise<void> {
		const accounts = this.msalService.instance.getAllAccounts();
		if (accounts.length === 0) {
			this._router.navigate(['/login']);
			return;
		}

		const account = accounts[0];
		this.msalService.instance.setActiveAccount(account);

		try {
			const result = await this.msalService.instance.acquireTokenSilent({
				scopes: environment.apiConfig.scopes,
				account: account
			});
			this.processAuthenticationResult(result);
		} catch (error) {
			console.error('Silent token acquisition failed:', error);
			// Fallback to interactive if silent fails
			this.msalService.loginRedirect({
				scopes: environment.apiConfig.scopes
			});
		}
	}

	private async processAuthenticationResult(result: AuthenticationResult): Promise<void> {
		if (!result || !result.accessToken) {
			return;
		}

		try {
			await this.createSessionVariablesFromMsal(result);
			this.loginService.isLoggedIn.next(true);
			this._router.navigate(['/home-page']);
		} catch (err) {
			console.error('Error creating session variables:', err);
			this._router.navigate(['/login']);
		}
	}

	private async createSessionVariablesFromMsal(authResult: AuthenticationResult) {
		const accessToken = authResult.accessToken;
		const idTokenClaims = authResult.idTokenClaims as any;
		const account = authResult.account;

		// Calculate token expiration
		const expiration = authResult.expiresOn
			? Math.trunc(authResult.expiresOn.getTime() / 1000)
			: Math.trunc(new Date().getTime() / 1000) + 3600;

		let loggedInUserLanguageCode = '';

		// Set base URLs in session storage
		sessionStorage.setItem('baseCommitmentUrl', environment.baseUrl + environment.commitmentUrl);
		sessionStorage.setItem('baseDealerServiceUrl', environment.baseUrl + environment.dealerServiceUrl);
		sessionStorage.setItem('baseAllocationUrl', environment.baseUrl + environment.wholesaleAllocationUrl);
		sessionStorage.setItem('baseBulkOrderCreationUrl', environment.baseUrl + environment.baseBulkOrderCreationUrl);
		sessionStorage.setItem('baseWersWholesaleAllocationUrl', environment.baseUrl + environment.wersWholesaleAllocationUrl);

		// Store tokens - accessToken is the bearer token for backend API calls
		sessionStorage.setItem('encodedIdToken', authResult.idToken || '');
		sessionStorage.setItem('encodedAccessToken', accessToken);
		sessionStorage.setItem('authToken', accessToken); // This is the bearer token for API calls
		sessionStorage.setItem('tokenExp', String(expiration));

		// Extract user info from Entra ID claims
		// Common Entra ID claims: preferred_username, name, oid, tid, etc.
		// Ford-specific claims may be in extension attributes
		const cdsId = this.extractCdsId(idTokenClaims, account);
		const siteCode = this.extractSiteCode(idTokenClaims);
		const countryCode = this.extractCountryCode(idTokenClaims);
		const preferredLanguage = this.extractPreferredLanguage(idTokenClaims);

		sessionStorage.setItem('cdsId', cdsId.toUpperCase());
		sessionStorage.setItem('loggedInSiteCode', siteCode);

		const getRolesFromApsUrl = sessionStorage.getItem('baseDealerServiceUrl') + WholesaleConstants.getApsRolesUrl;
		const roles: string[] = [];

		try {
			// Await the roles response first
			const rolesResponse = await this.loginService.getApsRoles(getRolesFromApsUrl);
			rolesResponse['RoleAssignment'].map(role => roles.push(role.RoleName));

			// Assign roleCode based on roles
			if (roles.includes('Zone Manager') || roles.includes('NSC')) {
				sessionStorage.setItem('roleCode', '1');
			} else if (roles.includes('Dealer Group')) {
				sessionStorage.setItem('roleCode', '3');
			} else if (roles.includes('CMA Dealer')) {
				sessionStorage.setItem('roleCode', '2');
			} else if (roles.includes('Product Team')) {
				sessionStorage.setItem('roleCode', '4');
			} else if (roles.includes('DemoUser')) {
				sessionStorage.setItem('roleCode', '5');
			} else {
				// Default role if no matching role found
				sessionStorage.setItem('roleCode', '5');
			}

			// Await the market language mapping data
			const marketLanguageMappingResponse: any[] = await this.getAllocationMarketDataService.getMarketLanguageMappingList().toPromise();
			this.marketLanguageMapping = marketLanguageMappingResponse;

			// Find market with the country code from token
			const filteredMarkets = this.marketLanguageMapping.filter(market => market.dealerMarket === countryCode);

			// Ensure marketInfo is set before accessing its properties
			if (filteredMarkets.length > 0) {
				this.marketInfo = filteredMarkets[0].marketInfo;
				sessionStorage.setItem('loggedInUserMarketCode', this.marketInfo.marketCode);
				sessionStorage.setItem('loggedInUserMarketText', this.marketInfo.marketText);

				// Determine language code based on role and market
				if (sessionStorage.getItem('roleCode') !== '2' && sessionStorage.getItem('roleCode') !== '3') {
					loggedInUserLanguageCode = 'en_GB';
				} else if (environment.production === true && countryCode === 'FRA' && preferredLanguage) {
					loggedInUserLanguageCode = preferredLanguage + '_' + preferredLanguage.toUpperCase();
				} else if (environment.production === true && countryCode === 'CHE' && preferredLanguage) {
					if (preferredLanguage === 'de') {
						loggedInUserLanguageCode = preferredLanguage + '_' + countryCode.toUpperCase().substring(0, 2);
					} else {
						loggedInUserLanguageCode = 'fr_' + countryCode.toUpperCase().substring(0, 2);
					}
				} else {
					// Use marketInfo locale if available
					if (this.marketInfo && this.marketInfo.locale !== undefined) {
						loggedInUserLanguageCode = this.marketInfo.locale;
					} else {
						loggedInUserLanguageCode = 'en_US';
					}
				}
				sessionStorage.setItem('loggedInUserLanguageCode', loggedInUserLanguageCode);
			} else {
				// No market found in mapping - set defaults
				sessionStorage.setItem('loggedInUserMarketCode', 'WAEGB');
				sessionStorage.setItem('loggedInUserMarketText', 'Ford of Britain');
				sessionStorage.setItem('loggedInUserLanguageCode', 'en_GB');
			}

			// Fetch Switzerland i18n data
			this.getAllocationMarketDataService.getSwitzerlandi18nList().subscribe((switzerlandResponse: any[]) => {
				this.switzerlandSelectLanguage = switzerlandResponse;
			});

		} catch (err) {
			console.error('Error during session variable creation:', err);
			// Set defaults so the app can still function
			sessionStorage.setItem('roleCode', '5');
			sessionStorage.setItem('loggedInUserMarketCode', 'WAEGB');
			sessionStorage.setItem('loggedInUserMarketText', 'Ford of Britain');
			sessionStorage.setItem('loggedInUserLanguageCode', 'en_GB');
		}
	}

	/**
	 * Extract CDS ID from Entra ID token claims
	 * Maps from Entra ID claims to the application's expected format
	 */
	private extractCdsId(idTokenClaims: any, account: any): string {
		// Try different claim names that might contain the CDS ID
		// Entra ID common claims: preferred_username, upn, email, name
		// Ford custom claims might be in extension attributes
		return idTokenClaims?.CommonName ||
			idTokenClaims?.extension_CommonName ||
			idTokenClaims?.onprem_sam_account_name ||
			idTokenClaims?.preferred_username?.split('@')[0] ||
			account?.username?.split('@')[0] ||
			idTokenClaims?.name ||
			'';
	}

	/**
	 * Extract Ford Site Code from Entra ID token claims
	 */
	private extractSiteCode(idTokenClaims: any): string {
		
		const fordSiteCode = idTokenClaims?.forddealersitecode ||
			'';
		if (fordSiteCode && fordSiteCode.length >= 8) {
			return fordSiteCode.substring(3, 8);
		}
		return fordSiteCode || '';
	}

	/**
	 * Extract country code from Entra ID token claims
	 * Uses the 'country' claim from the token
	 */
	private extractCountryCode(idTokenClaims: any): string {
		return idTokenClaims?.c ||
			idTokenClaims?.ctry ||
			idTokenClaims?.country ||
			'';
	}

	/**
	 * Extract preferred language from Entra ID token claims
	 */
	private extractPreferredLanguage(idTokenClaims: any): string {
		return idTokenClaims?.preferredLanguage ||
			idTokenClaims?.extension_preferredLanguage ||
			idTokenClaims?.locale ||
			'';
	}


	private validateSwitzerlandDealerId(idToken: any): any[] {
		const fordSiteCode = idToken?.forddealersitecode || '';
		const match = fordSiteCode.match(/\d+/g);
		return this.switzerlandSelectLanguage.filter(dealer => dealer.dealerId === (match ? match.toString() : ''));
	}

	private registerErrorHandler() {
		this.httpInterceptorService.errorStream$().subscribe(errorObj => {
			this.isServerError = true;
			if (errorObj.error.error) {
				this.errorMsg = errorObj.error.error;
			} else {
				this.errorMsg = errorObj.statusText;
			}

		});
	}

}

interface MarketInfoModel {
	marketCode: string;
	marketText: string;
	locale: string;
}
